
import React, { useEffect, useState } from 'react';
import { ApiKey, Project, apiService, createApiKey } from '../../services/apiService';
import ProjectHeader from './ProjectHeader';
import ProjectWalkthrough from './ProjectWalkthrough';


const ApiCallsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 6.1H3"/>
    <path d="M21 12.1H3"/>
    <path d="M15.5 18.1H3"/>
  </svg>
);

const TokensIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <path d="M12 6v12"/>
    <path d="M16 10H8"/>
  </svg>
);

const KeyIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>
  </svg>
);




interface OverviewTabProps {
  apiKey?: ApiKey;
  userId: string;
  onApiKeyCreated?: () => void;
}

export default function OverviewTab({ apiKey, userId, onApiKeyCreated }: OverviewTabProps) {
  const [usageStats, setUsageStats] = useState({ calls: 0, tokens: 0 });
  const [loading, setLoading] = useState(true);
  const [needsApiKey, setNeedsApiKey] = useState(false); 
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [, setProjects] = useState<Project[]>([]);
  const [newApiKey, setNewApiKey] = useState<ApiKey | null>(null);
  const [creatingKey, setCreatingKey] = useState(false);
  const [showWalkthrough, setShowWalkthrough] = useState(false);
  
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        if (!userId) {
          
          setLoading(false);
          return;
        }
        
        const userProjects = await apiService.getAllProjects(userId);
        
        setProjects(userProjects);
        
        if (userProjects.length > 0) {
          
          setCurrentProject(userProjects[0]);

          if (!apiKey) {
            
            setNeedsApiKey(true);
          } else {
            
            setNeedsApiKey(false);
            const { calls, tokens } = await apiService.fetchOverviewData(userId, userProjects[0].id);
            
            setUsageStats({ calls, tokens });
            
            if (calls === 0 && tokens === 0) {
              setShowWalkthrough(true);
            }
          }
        } else {
          
        }
      } catch (error) {
        
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [userId, apiKey]);

  const handleCreateApiKey = async () => {
    try {
      setCreatingKey(true);
      
      if (!userId) {
        
        throw new Error("User ID is required to create an API key");
      }
      
      if (!currentProject) {
        
        throw new Error("No project found");
      }
      
      
      const randomKey = crypto.randomUUID();
      const key = await createApiKey(userId, randomKey, currentProject.id);
      
      
      setNewApiKey(key);
      setNeedsApiKey(false);
      
      if (onApiKeyCreated) {
        onApiKeyCreated();
      }
    } catch (error) {
      
    } finally {
      setCreatingKey(false);
    }
  };
  
  const handleWalkthroughComplete = () => {
    setShowWalkthrough(false);
  };
  
  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading project data...</p>
      </div>
    );
  }
  
  if (needsApiKey && currentProject) {
    return (
      <div className="new-project-form" style={{ marginTop: '3rem' }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <KeyIcon />
          <h2 className="form-title" style={{ marginTop: '1.5rem' }}>Generate API Key</h2>
          <p className="form-description">
            Your project <strong>{currentProject.name}</strong> is set up, but you need an API key to start using it.
          </p>
        </div>
        
        <div className="dashboard-card">
          <h3 style={{ fontSize: '1.1rem', marginBottom: '1rem', fontWeight: '600' }}>
            API Key Details
          </h3>
          
          <p style={{ marginBottom: '1.5rem', fontSize: '0.95rem', color: 'var(--text-muted)' }}>
            This unique API key will allow your application to securely access the Abra platform. It should be treated as a secret and stored securely.
          </p>
          
          <p style={{ 
            background: 'var(--primary-light)', 
            color: 'var(--primary)', 
            padding: '1rem', 
            borderRadius: '6px', 
            marginBottom: '1.5rem' 
          }}>
            Once generated, store your API key in a secure location. For security reasons, we won't be able to show it again.
          </p>
          
          <button
            className="button-primary"
            onClick={handleCreateApiKey}
            disabled={creatingKey}
            style={{ width: '100%' }}
          >
            {creatingKey ? (
              <div className="button-loading">
                <span className="spinner"></span>
                <span>Generating API Key...</span>
              </div>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>
                </svg>
                <span>Generate API Key</span>
              </>
            )}
          </button>
        </div>
      </div>
    );
  }
  
  if (showWalkthrough && currentProject && (apiKey || newApiKey)) {
    const activeKey = newApiKey ? newApiKey.key : apiKey?.key || '';
    
    return (
      <ProjectWalkthrough 
        projectName={currentProject.name} 
        onComplete={handleWalkthroughComplete} 
        apiKey={activeKey}
      />
    );
  }
  
  return (
    <div className="overview-content">
      {currentProject && (
        <ProjectHeader project={currentProject} stats={usageStats} />
      )}
    
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-title">
            <ApiCallsIcon />
            Total API Calls
          </div>
          <div className="stat-value">{usageStats.calls.toLocaleString()}</div>
          {usageStats.calls > 0 && (
            <div className="stat-trend trend-up">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
                <polyline points="17 6 23 6 23 12"/>
              </svg>
              <span>Active</span>
            </div>
          )}
        </div>
        
        <div className="stat-card">
          <div className="stat-title">
            <TokensIcon />
            Total Tokens Used
          </div>
          <div className="stat-value">{usageStats.tokens.toLocaleString()}</div>
        </div>
      </div>
    </div>
  );
}